import { useEffect, useRef, useState } from 'react';

const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Ldf5gctAAAAAMQEl_r2-_m5Ggq-RUBeFrk1gMpZ';

let recaptchaScriptPromise;

function getRecaptcha() {
  return window.grecaptcha;
}

function waitForRecaptcha(resolve, reject, attempts = 0) {
  const recaptcha = getRecaptcha();

  if (recaptcha?.render) {
    resolve(recaptcha);
    return;
  }

  if (attempts > 80) {
    reject(new Error('Google reCAPTCHA did not finish loading.'));
    return;
  }

  window.setTimeout(() => waitForRecaptcha(resolve, reject, attempts + 1), 100);
}

function loadRecaptchaScript() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google reCAPTCHA can only load in the browser.'));
  }

  const recaptcha = getRecaptcha();
  if (recaptcha?.render) {
    return Promise.resolve(recaptcha);
  }

  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src^="${RECAPTCHA_SCRIPT_SRC}"]`);

      if (existingScript) {
        existingScript.addEventListener('load', () => waitForRecaptcha(resolve, reject), { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        waitForRecaptcha(resolve, reject);
        return;
      }

      const script = document.createElement('script');
      script.src = RECAPTCHA_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => waitForRecaptcha(resolve, reject);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  return recaptchaScriptPromise;
}

export default function GoogleRecaptcha({
  id,
  className = '',
  label = 'Security check',
  resetSignal = 0,
  onVerify,
  onExpire,
  onError,
}) {
  const recaptchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState(RECAPTCHA_SITE_KEY ? 'loading' : 'missing');

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      setStatus('missing');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    loadRecaptchaScript()
      .then((recaptcha) => {
        if (cancelled || !recaptchaRef.current) {
          return;
        }

        recaptchaRef.current.innerHTML = '';
        widgetIdRef.current = recaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => {
            setStatus('verified');
            onVerify?.(token);
          },
          'expired-callback': () => {
            setStatus('expired');
            onExpire?.();
          },
          'error-callback': () => {
            setStatus('error');
            onError?.();
          },
        });
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
          onError?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [onError, onExpire, onVerify]);

  useEffect(() => {
    const recaptcha = getRecaptcha();
    if (recaptcha?.reset && widgetIdRef.current !== null) {
      recaptcha.reset(widgetIdRef.current);
      setStatus('ready');
    }
  }, [resetSignal]);

  return (
    <div className={`recaptcha-field ${className}`.trim()}>
      <span className="recaptcha-field__label">{label}</span>
      <div id={id} ref={recaptchaRef} className="recaptcha-field__widget" />
      {status === 'loading' ? <span className="recaptcha-field__status">Loading ReCAPTCHA...</span> : null}
      {status === 'expired' ? <span className="recaptcha-field__status">ReCAPTCHA expired. Please check it again.</span> : null}
      {status === 'missing' ? <span className="recaptcha-field__status recaptcha-field__status--error">ReCAPTCHA site key is missing.</span> : null}
      {status === 'error' ? <span className="recaptcha-field__status recaptcha-field__status--error">ReCAPTCHA could not load. Please refresh and try again.</span> : null}
    </div>
  );
}
